import { useEffect, useState } from 'react'

import { filterSampleDrops } from '@/data/drops'
import { ApiError } from '@/lib/api/client'
import { listListings } from '@/lib/api/listings'
import type { ListingFilters, PublicListing } from '@/lib/types/listing'

/**
 * Filters the API can't apply yet. GET /api/listings takes only
 * q|brand|category|type and has no pagination, so its response is the complete
 * matching set — narrowing it here is exact, not a partial-page guess. When the
 * API gains `?country=`, move `country` into the server call and delete this.
 */
function applyClientFilters(
  rows: PublicListing[],
  filters: ListingFilters,
): PublicListing[] {
  return filters.country
    ? rows.filter((row) => row.country === filters.country)
    : rows
}

export function useListings(filters: ListingFilters = {}) {
  const [listings, setListings] = useState<PublicListing[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [usingSamples, setUsingSamples] = useState(false)

  const q = filters.q
  const brand = filters.brand
  const category = filters.category
  const type = filters.type
  const country = filters.country

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    const applied: ListingFilters = { q, brand, category, type, country }
    const serverFilters: ListingFilters = { q, brand, category, type }
    const serverFiltered = Boolean(q || brand || category || type)

    void (async () => {
      try {
        const rows = await listListings(serverFilters)
        if (cancelled) {
          return
        }
        const data = applyClientFilters(rows, applied)
        if (data.length > 0) {
          setListings(data)
          setUsingSamples(false)
          return
        }

        // `rows` already is the full catalog when nothing server-side applied.
        const catalog = serverFiltered ? await listListings() : rows
        if (cancelled) {
          return
        }

        if (catalog.length === 0) {
          setListings(filterSampleDrops(applied))
          setUsingSamples(true)
        } else {
          setListings([])
          setUsingSamples(false)
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setError(
            err instanceof ApiError ? err.detail : "Couldn't load links.",
          )
          setListings([])
          setUsingSamples(false)
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [q, brand, category, type, country])

  return { listings, loading, error, usingSamples }
}
